import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from '../../../components/Modal';
import { apiClient } from '../../../api/apiClient';
import { addDaysUtc, dayOfWeekUTC, daysInMonth, parseYmd, todayKST } from '../../../utils/date';
import {
  introStyle,
  mobileCss,
} from './CardPaymentDepositModal.styles';
import {
  CardDepositExportOptions,
  CardDepositFilters,
  CardDepositFooter,
  CardDepositSummary,
  CardDepositTable,
  compactDate,
} from './CardPaymentDepositModal.parts';

// 입금 계좌는 처음엔 빈 칸 — 목록을 받으면 이 회사가 저장한 기본값으로 채운다(서버 export_config, 2026-10-29).
//   예전엔 (주)파인그룹 실계좌가 여기 박혀 있어 미팅링크·손님 테넌트 창에도 떴다.
const EMPTY_EXPORT_CONFIG = { bankCode: '', accountNumber: '', accountHolder: '', memo: '' };

function getDefaultWeekRange() {
  const today = todayKST();
  const dow = dayOfWeekUTC(today);
  const mondayOffset = dow === 0 ? -6 : 1 - dow;
  const dateFrom = addDaysUtc(today, mondayOffset);
  return { dateFrom, dateTo: addDaysUtc(dateFrom, 4) };
}

function getDefaultMonthRange() {
  const today = todayKST();
  const { year, month } = parseYmd(today);
  const mm = String(month).padStart(2, '0');
  const lastDay = String(daysInMonth(year, month)).padStart(2, '0');
  return { dateFrom: `${year}-${mm}-01`, dateTo: `${year}-${mm}-${lastDay}` };
}

function buildQuery(params) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      qs.set(key, String(value));
    }
  });
  return qs.toString();
}

function downloadBlob(blob, fileName) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export default function CardPaymentDepositModal({ open, onClose }) {
  const defaultRange = useMemo(() => getDefaultWeekRange(), []);
  const defaultMonthRange = useMemo(() => getDefaultMonthRange(), []);
  const [dateFrom, setDateFrom] = useState(defaultRange.dateFrom);
  const [dateTo, setDateTo] = useState(defaultRange.dateTo);
  const [status, setStatus] = useState('');
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [exportConfig, setExportConfig] = useState(EMPTY_EXPORT_CONFIG);
  const [savingDefault, setSavingDefault] = useState(false);
  const [defaultSaved, setDefaultSaved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const query = buildQuery({ date_from: dateFrom, date_to: dateTo, status });
      const res = await apiClient.get(`/api/admin/reservations/card-deposits?${query}`);
      if (res.ok && res.data?.success) {
        setRows(res.data.data.rows || []);
        setSummary(res.data.data.summary || null);
        setExportConfig((prev) => ({ ...prev, ...(res.data.data.export_config || {}) }));
      } else {
        alert(res.data?.message || '카드입금 목록을 불러오지 못했습니다.');
      }
    } catch (error) {
      console.error('[CardPaymentDepositModal] load error', error);
      alert('카드입금 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, open, status]);

  useEffect(() => { load(); }, [load]);

  function updateRowMemo(id, memo) {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, card_deposit_memo: memo } : row)));
  }

  async function saveMemo(row) {
    setSavingId(row.id);
    try {
      const res = await apiClient.patch(`/api/admin/reservation-payments/${row.id}/card-deposit-memo`, {
        memo: row.card_deposit_memo || '',
      });
      if (!res.ok || !res.data?.success) {
        alert(res.data?.message || '메모 저장에 실패했습니다.');
        return;
      }
      await load();
    } finally {
      setSavingId(null);
    }
  }

  async function toggleConfirmed(row) {
    const confirmed = row.card_deposit_status !== 'confirmed';
    setSavingId(row.id);
    try {
      const res = await apiClient.patch(`/api/admin/reservation-payments/${row.id}/card-deposit`, {
        confirmed,
        memo: row.card_deposit_memo || '',
      });
      if (!res.ok || !res.data?.success) {
        alert(res.data?.message || '입금 상태 변경에 실패했습니다.');
        return;
      }
      await load();
    } finally {
      setSavingId(null);
    }
  }

  async function saveExportDefault() {
    setSavingDefault(true);
    try {
      const res = await apiClient.put('/api/admin/reservations/card-deposits/export-config', exportConfig);
      if (!res.ok || !res.data?.success) {
        alert(res.data?.message || '기본값 저장에 실패했습니다.');
        return;
      }
      setExportConfig({ ...EMPTY_EXPORT_CONFIG, ...(res.data.data || {}) });
      setDefaultSaved(true);
    } finally {
      setSavingDefault(false);
    }
  }

  async function downloadExcel() {
    // 입금 계좌가 비면 만들지 않는다(서버도 거절) — 다른 계좌로 대신 채우지 않음
    if (!exportConfig.bankCode.trim() || !exportConfig.accountNumber.trim() || !exportConfig.accountHolder.trim()) {
      alert('입금 계좌(은행코드·계좌번호·예금주)를 먼저 넣어 주세요.');
      return;
    }
    setDownloading(true);
    try {
      const query = buildQuery({
        date_from: dateFrom,
        date_to: dateTo,
        bank_code: exportConfig.bankCode,
        account_number: exportConfig.accountNumber,
        account_holder: exportConfig.accountHolder,
        memo: exportConfig.memo,
      });
      const res = await apiClient.download(`/api/admin/reservations/card-deposits/export?${query}`);
      if (!res.ok) {
        alert('엑셀 다운로드에 실패했습니다.');
        return;
      }
      downloadBlob(res.blob, `${compactDate(dateFrom)}-${compactDate(dateTo)}.xls`);
      await load();
    } finally {
      setDownloading(false);
    }
  }

  return (
    <Modal
      open={open}
      title="카드입금 관리"
      icon="🏦"
      onClose={onClose}
      footer={(
        <CardDepositFooter
          onClose={onClose}
          downloadExcel={downloadExcel}
          downloading={downloading}
          loading={loading}
        />
      )}
      width="min(1080px, calc(100vw - 32px))"
      mobileSheet
      mobileSheetDragClose
    >
      <style>{mobileCss}</style>
      <div style={introStyle}>
        <CardDepositFilters
          dateFrom={dateFrom}
          dateTo={dateTo}
          status={status}
          loading={loading}
          defaultRange={defaultRange}
          defaultMonthRange={defaultMonthRange}
          onDateFrom={setDateFrom}
          onDateTo={setDateTo}
          onStatus={setStatus}
          onLoad={load}
        />
        <CardDepositExportOptions
          exportConfig={exportConfig}
          setExportConfig={setExportConfig}
          onSaveDefault={saveExportDefault}
          savingDefault={savingDefault}
          defaultSaved={defaultSaved}
          onEdited={() => setDefaultSaved(false)}
        />
        <CardDepositSummary summary={summary} rows={rows} />
        <CardDepositTable
          rows={rows}
          savingId={savingId}
          updateRowMemo={updateRowMemo}
          toggleConfirmed={toggleConfirmed}
          saveMemo={saveMemo}
        />
      </div>
    </Modal>
  );
}
